import { useEffect, useState } from "react";
import { Gift, Copy, Check, Share2 } from "lucide-react";
import { memberApi } from "../api";
import { useAuth } from "../AuthContext";

export default function Referrals() {
  const { user } = useAuth();
  const [data, setData] = useState({ referral_code: user?.referral_code || "", referred_count: 0, referred: [] });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    memberApi
      .get("/api/members/me/referrals")
      .then((r) => setData(r.data))
      .catch((err) => console.error("Failed to load referrals:", err));
  }, []);

  const shareLink = typeof window !== "undefined"
    ? `${window.location.origin}/app/register?ref=${data.referral_code}`
    : `https://passionpilates.au/app/register?ref=${data.referral_code}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.warn("Clipboard copy failed, prompting manual copy:", err);
      window.prompt("Copy this link:", shareLink);
    }
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join me at Passion Pilates",
          text: `Sign up using my code ${data.referral_code} for an exclusive welcome offer!`,
          url: shareLink,
        });
      } catch (err) {
        // AbortError is fired when the user dismisses the share sheet — ignore it.
        if (err && err.name !== "AbortError") {
          console.warn("Share failed:", err);
        }
      }
    } else {
      copy();
    }
  };

  // Stable IDs for referral list (fall back to index if backend didn't send one)
  const referralItems = data.referred.map((r, i) => ({
    ...r,
    _key: r.id || r._id || r.email || `${r.name}-${r.created_at}-${i}`,
  }));

  return (
    <div className="space-y-6" data-testid="member-referrals-page">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-[#C97654] to-[#FF9472] mb-3 shadow-lg">
          <Gift className="w-7 h-7 text-white" />
        </div>
        <h2 className="text-2xl font-light text-[#E8DCC4]">Refer a Friend</h2>
        <p className="text-[#E8DCC4]/60 text-sm mt-1">Share Passion Pilates with friends &amp; family</p>
      </div>

      <div className="bg-gradient-to-br from-[#1a1a1a] to-[#2a1115] border-2 border-[#C97654] rounded-2xl p-6 text-center" data-testid="referral-code-card">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#C97654] mb-2 font-bold">Your Code</p>
        <p className="text-4xl font-bold text-[#E8DCC4] tracking-widest mb-4">{data.referral_code || "—"}</p>

        <div className="bg-[#0a0a0a] rounded-lg px-3 py-2 mb-4 text-xs text-[#E8DCC4]/70 break-all">
          {shareLink}
        </div>

        <div className="flex gap-2">
          <button
            onClick={share}
            data-testid="referral-share-btn"
            className="flex-1 bg-gradient-to-r from-[#C97654] to-[#FF9472] text-white py-3 rounded-lg font-bold text-sm flex items-center justify-center gap-2 shadow-md active:scale-95 transition-transform"
          >
            <Share2 className="w-4 h-4" />
            Share Link
          </button>
          <button
            onClick={copy}
            data-testid="referral-copy-btn"
            className="bg-[#8B2635] hover:bg-[#a03042] text-white py-3 px-4 rounded-lg font-bold text-sm flex items-center justify-center gap-2 transition-colors"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
      </div>

      <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl p-5">
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="text-[#E8DCC4] font-medium">Friends Referred</h3>
          <span className="text-[#C97654] font-bold text-2xl">{data.referred_count}</span>
        </div>
        {data.referred.length === 0 ? (
          <p className="text-[#E8DCC4]/50 text-sm">No referrals yet — share your code to get started!</p>
        ) : (
          <ul className="space-y-2">
            {referralItems.map((r) => (
              <li key={r._key} className="flex justify-between items-center text-sm py-2 border-b border-[#2a2a2a] last:border-0">
                <span className="text-[#E8DCC4]">{r.name}</span>
                <span className="text-[#E8DCC4]/40 text-xs">{new Date(r.created_at).toLocaleDateString()}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
